import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import axiosInstance from "../features/axiosInstance/axiosInstance";
import {
  MilestoneHealthFinding,
  MilestoneHealthReportResponse,
  MilestoneHealthRow,
} from "../features/milestoneHealth/MilestoneHealthReport";

const severityColor = (
  severity: string
): "default" | "info" | "warning" | "error" => {
  switch (severity) {
    case "critical":
      return "error";
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "default";
  }
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const deviationColor = (pct: number): string => {
  if (pct > 25) return "error.main";
  if (pct > 10) return "warning.main";
  if (pct < -25) return "info.main";
  return "text.primary";
};

interface FindingsBreakdownProps {
  findings: MilestoneHealthFinding[];
}

const FindingsBreakdown = ({ findings }: FindingsBreakdownProps) => {
  if (findings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No deviations detected.
      </Typography>
    );
  }

  const grouped = findings.reduce<Record<string, MilestoneHealthFinding[]>>(
    (acc, f) => {
      (acc[f.category] ||= []).push(f);
      return acc;
    },
    {}
  );

  return (
    <Box sx={{ p: 2 }}>
      {Object.entries(grouped).map(([category, items]) => (
        <Box key={category} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {category} ({items.length})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "80px" }}>Severity</TableCell>
                <TableCell sx={{ width: "100px" }}>Task #</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((f, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Chip
                      label={f.severity}
                      size="small"
                      color={severityColor(f.severity)}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {f.taskNumber || "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.85rem" }}>
                    {f.taskTitle}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                    {f.detail}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </Box>
  );
};

interface MilestoneRowProps {
  row: MilestoneHealthRow;
  expanded: boolean;
  onToggle: () => void;
}

const MilestoneRow = ({ row, expanded, onToggle }: MilestoneRowProps) => {
  const totalFindings = row.findings.length;
  const critical = row.findings.filter((f) => f.severity === "critical").length;

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }} onClick={onToggle}>
        <TableCell sx={{ width: "50px" }}>
          <IconButton size="small">
            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 500 }}>{row.milestoneName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.projectName} · {row.state}
          </Typography>
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: "monospace" }}>
          {row.totalTasks}
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Tasks added in last 20% of milestone window">
            <span>{row.lateAddedTasks}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{row.tasksWithoutEstimate}</TableCell>
        <TableCell align="right">{row.tasksOpenAtClose}</TableCell>
        <TableCell align="right">
          <Tooltip title="Tasks with estimate but zero time logged">
            <span>{row.tasksWithEstimateButNoTimeEntries}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{row.stalledTasks}</TableCell>
        <TableCell align="right">{row.overnightTimeEntries}</TableCell>
        <TableCell align="right" sx={{ fontFamily: "monospace" }}>
          <Tooltip title="Latest sprint total estimate (TotalEstimateDataPoint)">
            <span>
              {row.sprintTotalEstimateHours > 0
                ? row.sprintTotalEstimateHours.toFixed(1)
                : "—"}
            </span>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sx={{ fontFamily: "monospace" }}>
          {row.actualHours.toFixed(1)}
        </TableCell>
        <TableCell
          align="right"
          sx={{
            fontFamily: "monospace",
            fontWeight: 600,
            color:
              row.sprintExceededHours > 0 ? "error.main" : "text.primary",
          }}
        >
          <Tooltip title="Actual hours above the sprint total estimate">
            <span>
              {row.sprintTotalEstimateHours > 0
                ? row.sprintExceededHours > 0
                  ? `+${row.sprintExceededHours.toFixed(1)}`
                  : "0"
                : "—"}
            </span>
          </Tooltip>
        </TableCell>
        <TableCell
          align="right"
          sx={{
            fontFamily: "monospace",
            fontWeight: 600,
            color:
              row.sprintTotalEstimateHours > 0
                ? deviationColor(row.sprintDeviationPct)
                : "text.secondary",
          }}
        >
          {row.sprintTotalEstimateHours > 0
            ? `${row.sprintDeviationPct > 0 ? "+" : ""}${row.sprintDeviationPct.toFixed(0)}%`
            : "—"}
        </TableCell>
        <TableCell align="right">
          {critical > 0 ? (
            <Chip
              label={`${critical}!`}
              size="small"
              color="error"
              sx={{ mr: 0.5 }}
            />
          ) : null}
          <Chip
            label={totalFindings}
            size="small"
            color={totalFindings > 0 ? "warning" : "default"}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={14}
          sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}
        >
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ pl: 4, pr: 2, py: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexWrap: "wrap",
                  mb: 1,
                  fontSize: "0.85rem",
                  color: "text.secondary",
                }}
              >
                <span>
                  <strong>Created:</strong> {formatDate(row.gitLabCreateDate)}
                </span>
                <span>
                  <strong>First task:</strong> {formatDate(row.firstTaskCreated)}
                </span>
                <span>
                  <strong>Last closed task:</strong>{" "}
                  {formatDate(row.lastTaskClosed)}
                </span>
                <span>
                  <strong>Duration:</strong> {row.durationDays}d
                </span>
                <span>
                  <strong>Final estimate:</strong>{" "}
                  {row.finalEstimateHours.toFixed(1)}h
                </span>
                <span>
                  <strong>Estimate accuracy:</strong>{" "}
                  {row.estimateAccuracyPct.toFixed(0)}%
                </span>
                <span>
                  <strong>Estimate churn tasks:</strong> {row.estimateChurnTasks}
                </span>
                <span>
                  <strong>Oversized sessions:</strong> {row.oversizedSessions}
                </span>
                <span>
                  <strong>Edited time entries:</strong> {row.editedTimeEntries}
                </span>
              </Box>
              <FindingsBreakdown findings={row.findings} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const MilestoneHealthReportPage = () => {
  const [report, setReport] = useState<MilestoneHealthReportResponse | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [projectFilter, setProjectFilter] = useState<number | "all">("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get<MilestoneHealthReportResponse>("/milestonehealth")
      .then((response) => {
        if (!cancelled) setReport(response.data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load milestone health report:", err);
          setError(
            err?.response?.status === 403
              ? "Access denied — admin only."
              : "Failed to load report."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const projects = useMemo(() => {
    if (!report) return [];
    const map = new Map<number, string>();
    report.milestones.forEach((m) => map.set(m.projectId, m.projectName));
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [report]);

  const filtered = useMemo(() => {
    if (!report) return [];
    if (projectFilter === "all") return report.milestones;
    return report.milestones.filter((m) => m.projectId === projectFilter);
  }, [report, projectFilter]);

  const summary = useMemo(() => {
    if (filtered.length === 0) return null;
    return {
      milestones: filtered.length,
      totalFindings: filtered.reduce((s, m) => s + m.findings.length, 0),
      critical: filtered.reduce(
        (s, m) => s + m.findings.filter((f) => f.severity === "critical").length,
        0
      ),
      sprintExceeded: filtered.filter((m) => m.sprintExceededHours > 0).length,
      totalSprintExceeded: filtered.reduce(
        (s, m) => s + m.sprintExceededHours,
        0
      ),
    };
  }, [filtered]);

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Container maxWidth="xl" sx={{ pt: 4, pb: 6, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: 700, letterSpacing: "-0.5px", mb: 1 }}
        >
          Milestone Health Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Admin-only view of deviations across all milestones: late-added tasks,
          missing estimates, stalled work, time-entry anomalies, and budget
          overruns.
        </Typography>
      </Box>

      {summary && (
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Chip label={`${summary.milestones} milestones`} />
          <Chip
            label={`${summary.totalFindings} findings`}
            color={summary.totalFindings > 0 ? "warning" : "default"}
          />
          <Chip
            label={`${summary.critical} critical`}
            color={summary.critical > 0 ? "error" : "default"}
          />
          <Chip
            label={`${summary.sprintExceeded} exceeded sprint estimate`}
            color={summary.sprintExceeded > 0 ? "error" : "default"}
          />
          <Chip
            label={`+${summary.totalSprintExceeded.toFixed(1)}h total exceeded`}
            color={summary.totalSprintExceeded > 0 ? "error" : "default"}
          />
        </Box>
      )}

      <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Project</InputLabel>
          <Select
            label="Project"
            value={projectFilter}
            onChange={(e) =>
              setProjectFilter(
                e.target.value === "all" ? "all" : Number(e.target.value)
              )
            }
          >
            <MenuItem value="all">All projects</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      ) : (
        <Paper elevation={3} sx={{ overflow: "hidden" }}>
          <TableContainer>
            <Table size="small" aria-label="milestone health report">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "50px" }} />
                  <TableCell sx={{ fontWeight: 600 }}>Milestone</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Tasks
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Late
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    No est.
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Open@close
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    No time
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Stalled
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Overnight
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Sprint est. h
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Actual h
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Exceeded h
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Δ%
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Findings
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No milestones.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => (
                    <MilestoneRow
                      key={row.milestoneId}
                      row={row}
                      expanded={expanded.has(row.milestoneId)}
                      onToggle={() => toggle(row.milestoneId)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default MilestoneHealthReportPage;
